import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getWorkflow, DigitalCheckinWorkflowResponse, WorkflowStepInfo, WorkflowType, StepStatus } from '../api/athena';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/apiError';

interface WorkflowContextType {
  workflow: DigitalCheckinWorkflowResponse | null;
  loading: boolean;
  error: string | null;
  refreshWorkflow: () => Promise<void>;
  markStepCompleted: (predicate: (step: WorkflowStepInfo) => boolean) => void;
  getStepByUrl: (url: string) => WorkflowStepInfo | undefined;
  getStepByRouteKey: (routeKey: string) => WorkflowStepInfo | undefined;
  getNextPendingStep: () => WorkflowStepInfo | undefined;
  getNextStepInSequence: (currentUrl: string) => WorkflowStepInfo | undefined;
  getPreviousStep: (currentUrl: string) => WorkflowStepInfo | undefined;
  isAllStepsCompleted: () => boolean;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

// Constants - extracted outside component to avoid recreation. Exported so other pages can
// derive position info (e.g. "is this the last demographics sub-page?") from the same source
// of truth — otherwise reordering pages here silently leaves stale per-page logic behind.
export const DEMOGRAPHIC_PAGE_ORDER = [
  'personal-information',
  'contact-and-communication',
  'address-and-identity',
  'emergency-and-billing',
  'care-and-insurance',
] as const;

export type DemographicSubPage = typeof DEMOGRAPHIC_PAGE_ORDER[number];

export const isLastDemographicSubPage = (page: DemographicSubPage): boolean =>
  DEMOGRAPHIC_PAGE_ORDER[DEMOGRAPHIC_PAGE_ORDER.length - 1] === page;

const WORKFLOW_TYPE_ORDER = [
  WorkflowType.DEMOGRAPHIC,
  WorkflowType.HEALTH_HISTORY_FORM,
  WorkflowType.SCREENERS,
  WorkflowType.CONSENT_FORM,
] as const;

const ROUTE_TO_WORKFLOW_TYPE: Record<string, WorkflowType> = {
  'personal-information': WorkflowType.DEMOGRAPHIC,
  'contact-and-communication': WorkflowType.DEMOGRAPHIC,
  'address-and-identity': WorkflowType.DEMOGRAPHIC,
  'emergency-and-billing': WorkflowType.DEMOGRAPHIC,
  'care-and-insurance': WorkflowType.DEMOGRAPHIC,
  'medical-history': WorkflowType.HEALTH_HISTORY_FORM,
  'wellness-check': WorkflowType.SCREENERS,
  'review-and-sign': WorkflowType.CONSENT_FORM,
};

interface WorkflowProviderProps {
  children: React.ReactNode;
}

const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children }) => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [workflow, setWorkflow] = useState<DigitalCheckinWorkflowResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightFetchRef = useRef<Promise<void> | null>(null);

  const fetchWorkflow = useCallback((): Promise<void> => {
    if (!appointmentId) {
      setError("Missing appointment id");
      return Promise.resolve();
    }

    // If a fetch is already in flight, share its promise so concurrent
    // callers all resolve when it finishes — never silently dropped.
    if (inFlightFetchRef.current) {
      return inFlightFetchRef.current;
    }

    setLoading(true);
    setError(null);

    const promise = (async () => {
      try {
        const data = await getWorkflow(appointmentId);
        setWorkflow(data);
        setError(null);
      } catch (err: any) {
        const statusCode = err?.response?.status;
        const errorCode = getApiErrorCode(err);
        if (statusCode === 409 && errorCode === 'WORKFLOW_NOT_CREATED') {
          setError("Check-in has not yet started.");
        } else {
          setError(getApiErrorMessage(err, "Unable to load workflow information. Please try again."));
        }
        console.error("Failed to fetch workflow", err);
      } finally {
        setLoading(false);
        inFlightFetchRef.current = null;
      }
    })();

    inFlightFetchRef.current = promise;
    return promise;
  }, [appointmentId]);

  useEffect(() => {
    if (appointmentId && !workflow) {
      fetchWorkflow();
    }
  }, [appointmentId, fetchWorkflow, workflow]);

  const refreshWorkflow = useCallback((): Promise<void> => {
    if (!appointmentId) {
      setError("Missing appointment id");
      return Promise.resolve();
    }
    return fetchWorkflow();
  }, [appointmentId, fetchWorkflow]);

  // Optimistically mark every step matching `predicate` as COMPLETED in local
  // state, so the progress bar reflects user progress immediately without
  // waiting for the next workflow refetch. Reconciled by the next mount's
  // refreshWorkflow().
  const markStepCompleted = useCallback(
    (predicate: (step: WorkflowStepInfo) => boolean) => {
      setWorkflow(prev => {
        if (!prev) return prev;
        let mutated = false;
        const nextSteps = prev.steps.map(step => {
          if (
            predicate(step) &&
            step.status !== StepStatus.COMPLETED &&
            step.status !== StepStatus.SYNCED
          ) {
            mutated = true;
            return {
              ...step,
              status: StepStatus.COMPLETED,
              completedAt: new Date().toISOString(),
            };
          }
          return step;
        });
        return mutated ? { ...prev, steps: nextSteps } : prev;
      });
    },
    [],
  );

  // Helper function to get step by stepUrl
  const getStepByUrl = useCallback((url: string): WorkflowStepInfo | undefined => {
    if (!workflow?.steps) return undefined;
    return workflow.steps.find(step => step.stepUrl === url);
  }, [workflow]);

  // Helper function to get step by route key (e.g., "personal-information")
  const getStepByRouteKey = useCallback((routeKey: string): WorkflowStepInfo | undefined => {
    if (!workflow?.steps) return undefined;
    return workflow.steps.find(step => {
      const urlParts = step.stepUrl.split('/');
      const lastPart = urlParts[urlParts.length - 1];
      return lastPart === routeKey;
    });
  }, [workflow]);

  // Helper: Extract route key from URL
  const extractRouteKey = useCallback((url: string): string => {
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1];
  }, []);

  // Helper: Group and sort steps by workflow type
  const getStepsByType = useCallback((): Map<WorkflowType, WorkflowStepInfo[]> => {
    if (!workflow?.steps) return new Map();
    
    const stepsByType = new Map<WorkflowType, WorkflowStepInfo[]>();
    workflow.steps.forEach(step => {
      if (!stepsByType.has(step.workflowType)) {
        stepsByType.set(step.workflowType, []);
      }
      stepsByType.get(step.workflowType)!.push(step);
    });

    // Sort steps within each type by displayOrder
    stepsByType.forEach((steps) => {
      steps.sort((a, b) => a.displayOrder - b.displayOrder);
    });

    return stepsByType;
  }, [workflow]);

  // Helper: Find step by route key and workflow type
  const findStepByRouteKey = useCallback((
    routeKey: string, 
    workflowType: WorkflowType
  ): WorkflowStepInfo | undefined => {
    if (!workflow?.steps) return undefined;
    return workflow.steps.find(step => {
      const stepRouteKey = extractRouteKey(step.stepUrl);
      return stepRouteKey === routeKey && step.workflowType === workflowType;
    });
  }, [workflow, extractRouteKey]);

  // Helper function to get next pending step (first pending step across all workflow types)
  const getNextPendingStep = useCallback((): WorkflowStepInfo | undefined => {
    const stepsByType = getStepsByType();
    
    // Find first pending step following the fixed order
    for (const type of WORKFLOW_TYPE_ORDER) {
      const steps = stepsByType.get(type) || [];
      const pendingStep = steps.find(step => 
        step.status === 'PENDING' || step.status === 'IN_PROGRESS'
      );
      if (pendingStep) {
        return pendingStep;
      }
    }

    return undefined;
  }, [getStepsByType]);

  // Helper function to get next step in sequence (within same workflow type, then next workflow type)
  const getNextStepInSequence = useCallback((currentUrl: string): WorkflowStepInfo | undefined => {
    if (!workflow?.steps) return undefined;
    const currentRouteKey = extractRouteKey(currentUrl);
    const currentWorkflowType = ROUTE_TO_WORKFLOW_TYPE[currentRouteKey];
    if (!currentWorkflowType) return undefined;

    const stepsByType = getStepsByType();

    // Handle DEMOGRAPHIC workflow type: navigate through pages in sequence
    if (currentWorkflowType === WorkflowType.DEMOGRAPHIC) {
      const currentIndex = DEMOGRAPHIC_PAGE_ORDER.indexOf(currentRouteKey as typeof DEMOGRAPHIC_PAGE_ORDER[number]);
      if (currentIndex >= 0 && currentIndex < DEMOGRAPHIC_PAGE_ORDER.length - 1) {
        const nextRouteKey = DEMOGRAPHIC_PAGE_ORDER[currentIndex + 1];
        return findStepByRouteKey(nextRouteKey, WorkflowType.DEMOGRAPHIC);
      }
      // If no more demographic pages, fall through to next workflow type
    }

    // Move to next workflow type (demographic, health history, screener, consent may or may not exist)
    const currentTypeIndex = WORKFLOW_TYPE_ORDER.indexOf(currentWorkflowType);
    if (currentTypeIndex < 0) {
      return undefined;
    }

    // Check each subsequent workflow type for steps; return first step found
    for (let i = currentTypeIndex + 1; i < WORKFLOW_TYPE_ORDER.length; i++) {
      const nextWorkflowType = WORKFLOW_TYPE_ORDER[i];
      const nextTypeSteps = stepsByType.get(nextWorkflowType) || [];
      if (nextTypeSteps.length > 0) {
        return nextTypeSteps[0]; // First step of this type (already sorted)
      }
    }

    // No remaining workflow type has steps — return completion page
    const completionStepUrl = appointmentId ? `/appointments/${appointmentId}/complete` : '/complete';
    return {
      stepUrl: completionStepUrl,
    } as WorkflowStepInfo;
  }, [workflow, appointmentId, extractRouteKey, getStepsByType, findStepByRouteKey]);

  // Helper: Build ordered list of all step URLs respecting demographic page order
  const getOrderedStepUrls = useCallback((): string[] => {
    if (!workflow?.steps) return [];
    
    const stepsByType = getStepsByType();
    const uniqueStepUrls: string[] = [];
    const seenUrls = new Set<string>();
    
    for (const type of WORKFLOW_TYPE_ORDER) {
      if (type === WorkflowType.DEMOGRAPHIC) {
        // Use demographic page order
        for (const routeKey of DEMOGRAPHIC_PAGE_ORDER) {
          const step = findStepByRouteKey(routeKey, WorkflowType.DEMOGRAPHIC);
          if (step && !seenUrls.has(step.stepUrl)) {
            uniqueStepUrls.push(step.stepUrl);
            seenUrls.add(step.stepUrl);
          }
        }
      } else {
        // For other types, use steps from workflow (already sorted)
        const steps = stepsByType.get(type) || [];
        for (const step of steps) {
          if (!seenUrls.has(step.stepUrl)) {
            uniqueStepUrls.push(step.stepUrl);
            seenUrls.add(step.stepUrl);
          }
        }
      }
    }
    
    return uniqueStepUrls;
  }, [workflow, getStepsByType, findStepByRouteKey]);

  // Helper function to get previous step
  const getPreviousStep = useCallback((currentUrl: string): WorkflowStepInfo | undefined => {
    if (!workflow?.steps) return undefined;

    const currentRouteKey = extractRouteKey(currentUrl);
    const currentWorkflowType = ROUTE_TO_WORKFLOW_TYPE[currentRouteKey];
    
    if (!currentWorkflowType) return undefined;

    // Handle DEMOGRAPHIC workflow type: navigate through pages in sequence
    if (currentWorkflowType === WorkflowType.DEMOGRAPHIC) {
      const currentIndex = DEMOGRAPHIC_PAGE_ORDER.indexOf(currentRouteKey as typeof DEMOGRAPHIC_PAGE_ORDER[number]);
      if (currentIndex > 0) {
        const previousRouteKey = DEMOGRAPHIC_PAGE_ORDER[currentIndex - 1];
        return findStepByRouteKey(previousRouteKey, WorkflowType.DEMOGRAPHIC);
      }
      // If on first demographic page, no previous step
      return undefined;
    }

    // For other workflow types, find previous step across workflow types
    const orderedUrls = getOrderedStepUrls();
    const currentIndex = orderedUrls.indexOf(currentUrl);
    
    if (currentIndex <= 0) return undefined;

    const previousUrl = orderedUrls[currentIndex - 1];
    return getStepByUrl(previousUrl);
  }, [workflow, extractRouteKey, findStepByRouteKey, getOrderedStepUrls, getStepByUrl]);

  // Helper function to check if all steps are completed
  const isAllStepsCompleted = useCallback((): boolean => {
    if (!workflow?.steps) return false;
    return workflow.steps.every(step => 
      step.status === 'COMPLETED' || step.status === 'SYNCED'
    );
  }, [workflow]);

  const value: WorkflowContextType = {
    workflow,
    loading,
    error,
    refreshWorkflow,
    markStepCompleted,
    getStepByUrl,
    getStepByRouteKey,
    getNextPendingStep,
    getNextStepInSequence,
    getPreviousStep,
    isAllStepsCompleted,
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};

const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error("useWorkflow must be used within a WorkflowProvider");
  }
  return context;
};

export { WorkflowProvider, useWorkflow };
